import React from "react";
import { useMediaQuery } from "@mui/system";
import { Box, Table, TableBody, TableContainer } from "@mui/material";
import { SectionTitle } from "features/SectionTitle";
import {
  Button,
  DateField,
  FunctionField,
  RecordContextProvider,
  ReferenceField,
  TextField,
  useNotify,
  useRecordContext,
} from "react-admin";
import { settings } from "app";
import { employeeRequests } from "api/employeeApi";
import { documentRequests } from "api/documentsApi";
import EmployeesField from "server/components/Fields/EmployeesField";
import { getFile, previewFileFromUrl } from "server/components/DownloadFile";
import { DetailsLeaveType, StaffRequestTypes } from "utils/constants";
import InfoRow from "./InfoRow";

const DocumentActions = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const documentId = record?.documentId;

  if (!documentId) return null;

  const handlePreview = () =>
    previewFileFromUrl(
      `${settings.gatewayApi}/${documentRequests.getDocumentById(documentId, "private")}`,
      `document-${documentId}`,
      notify,
    );
  const handleDownload = () => getFile(documentId, notify, "private", true);

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Button
        size="small"
        variant="outlined"
        label="Преглед"
        onClick={handlePreview}
      />
      <Button
        size="small"
        variant="outlined"
        label="Изтегляне"
        onClick={handleDownload}
      />
    </Box>
  );
};

// Vacations return a `documents` array; sick leaves return a single top-level
// `documentId`/`documentNumber`. Normalize both to one list so either renders.
const getRecordDocuments = record => {
  if (record?.documents?.length > 0) return record.documents;
  if (record?.documentId) return [{ documentId: record.documentId, description: record.documentNumber }];
  return [];
};

// Files attached from the node-leaves list arrive in their own `attachments` array and name the
// label `fileName`. Normalize to the same { documentId, description } shape as the documents.
const getRecordAttachments = record =>
  (record?.attachments ?? []).map(({ documentId, fileName }) => ({ documentId, description: fileName }));

const FilesField = ({ files }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    {files.map((file, index) => (
      <RecordContextProvider
        key={index}
        value={file}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <span>{file.description}</span>
          <DocumentActions />
        </Box>
      </RecordContextProvider>
    ))}
  </Box>
);

const PeriodsField = () => {
  const record = useRecordContext();
  const periods = record?.periods ?? [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {periods.map((period, index) => {
        const from = period.dateFrom ? new Date(period.dateFrom).toLocaleDateString("bg-BG") : "";
        const to = period.dateTo ? new Date(period.dateTo).toLocaleDateString("bg-BG") : "";
        const days = period.numberOfDays != null ? ` (${period.numberOfDays} дни)` : "";
        return (
          <Box
            key={index}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <span>
              {from} - {to}
              {days}
            </span>
            {period.substituteEmployeeId && (
              <RecordContextProvider value={period}>
                <span>— Заместник: </span>
                <EmployeesField source="substituteEmployeeId" />
              </RecordContextProvider>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// The details endpoint names the identifier `contractId`, while the contracts select the request
// was built from returns `contractNumber` — accept either.
const ContractsField = () => {
  const record = useRecordContext();
  const contracts = record?.contracts ?? [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {contracts.map((contract, index) => (
        <span key={contract.contractId ?? contract.contractNumber ?? index}>
          {[
            `Договор № ${contract.contractId ?? contract.contractNumber}`,
            contract.facultyName,
            contract.positionName,
          ]
            .filter(Boolean)
            .join(" — ")}
        </span>
      ))}
    </Box>
  );
};

const LeaveInfoSection = ({ type }) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const record = useRecordContext();
  const documents = getRecordDocuments(record);
  const attachments = getRecordAttachments(record);
  const contracts = record?.contracts ?? [];
  const isSickLeave = type === StaffRequestTypes.SickLeave;
  const isVacation = type === StaffRequestTypes.AnnualLeave;
  const leaveTypeName = DetailsLeaveType.find(x => x.id === type)?.name;

  return (
    <>
      <SectionTitle
        title={`Данни за заявката${leaveTypeName ? `: ${leaveTypeName}` : ""}`}
        textAlign="center"
        sx={{ marginTop: "0.5rem" }}
      />
      <Box sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}>
        <TableContainer>
          <Table size="small">
            <TableBody>
              <InfoRow label="Служител">
                <ReferenceField
                  source="employeeId"
                  reference={employeeRequests.getEmployeesSelect()}
                  link={false}
                >
                  <FunctionField
                    render={rec =>
                      rec.secondName
                        ? `${rec.firstName}  ${rec.secondName} ${rec.lastName}`
                        : `${rec.firstName} ${rec.lastName}`
                    }
                  />
                </ReferenceField>
              </InfoRow>

              {isSickLeave && (
                <>
                  <InfoRow label="Тип болничен">
                    <ReferenceField
                      source="sickLeaveType"
                      reference={employeeRequests.getSickLeaveTypesSelect()}
                      link={false}
                    >
                      <TextField source="nameBg" />
                    </ReferenceField>
                  </InfoRow>
                  <InfoRow label="От дата">
                    <DateField
                      source="dateFrom"
                      locales="bg-BG"
                    />
                  </InfoRow>
                  <InfoRow label="До дата">
                    <DateField
                      source="dateTo"
                      locales="bg-BG"
                    />
                  </InfoRow>
                </>
              )}

              {isVacation && (
                <>
                  <InfoRow label="Основание">
                    <ReferenceField
                      source="reasonId"
                      reference={employeeRequests.getLeaveReasonsSelect()}
                      link={false}
                    >
                      <FunctionField
                        render={rec =>
                          rec.note ? rec.note : rec.name
                        }
                      />
                    </ReferenceField>
                  </InfoRow>
                  <InfoRow label="Периоди">
                    <PeriodsField />
                  </InfoRow>
                  <InfoRow label="Коментар">
                    <TextField source="comment" />
                  </InfoRow>
                </>
              )}

              {contracts.length > 0 && (
                <InfoRow label="Трудови договори">
                  <ContractsField />
                </InfoRow>
              )}

              {documents.length > 0 && (
                <InfoRow label="Документи">
                  <FilesField files={documents} />
                </InfoRow>
              )}

              {attachments.length > 0 && (
                <InfoRow label="Прикачени файлове">
                  <FilesField files={attachments} />
                </InfoRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default LeaveInfoSection;
